import { ChangeDetectionStrategy, Component, OnInit, computed, signal } from '@angular/core';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { DietDb } from 'lib/cosmos/diet/dietDb.js';
import { ExerciseDb } from 'lib/cosmos/exercise/exerciseDb.js';
import { FoodDb } from 'lib/cosmos/food/foodDb.js';
import { PackageDb } from 'lib/cosmos/package/packageDb.js';
import { ProgressTypeDb } from 'lib/cosmos/progress-type/progressTypeDb.js';
import { TaskDb } from 'lib/cosmos/task/taskDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { AdminUserDataOverview } from 'lib/cosmos/user/userService.js';
import { WorkoutDb } from 'lib/cosmos/workout/workoutDb.js';
import { isNotUndefined } from 'lib/helpers/array.utils';
import { IButton } from 'lib/ui/buttons';
import { SimpleTableItem } from 'lib/ui/simple-table/index.js';
import { ITable, ITableField } from 'lib/ui/table/index.js';
import { IImage } from 'lib/ui/ui-models.js';
import { map } from 'rxjs';
import { ItemType } from 'src/app/core/core.models.js';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';
import { userAdminHelper } from './helpers/admin-user-helper';

type OverviewTable = {
	readonly table: ITable<any>;
	readonly id: string;
	readonly title: string;
	readonly suffix: string;
};

@Component({
	selector: 'mod-view-admin-user',
	templateUrl: './view-admin-user.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ViewAdminUserComponent extends TypeCoreComponent<UserDb.IUserItem> implements OnInit {
	public readonly userOverview = signal<AdminUserDataOverview | undefined>(undefined);
	public readonly userItems = computed<readonly SimpleTableItem[]>(() => {
		const user = this.sharedData()?.data;
		if (!user) {
			return [];
		}

		const items: readonly SimpleTableItem[] = userAdminHelper.getOverUserSimpleTableItems(
			user,
			this.dependencies.timeService
		);

		return items;
	});

	public readonly brandingItems = computed<readonly SimpleTableItem[]>(() => {
		const branding = this.sharedData()?.data?.branding;
		if (!branding) {
			return [];
		}

		const items: readonly SimpleTableItem[] = [
			{ title: 'Brand name', value: branding.brandName, valueType: 'text' },
			{
				title: 'Logo',
				value: undefined,
				images: branding.brandLogo
					?.map((logo) => logo.image)
					.filter(isNotUndefined)
					.map((m) => ({ image: m, title: 'Brand logo' })),
				valueType: 'text'
			},
			{ title: 'Subdomain', value: branding.brandingSubdomain, valueType: 'text' },
			{ title: 'Bio', value: branding.brandBio, valueType: 'html' },
			{ title: 'Tel', value: branding.brandTel, valueType: 'text' }
		];

		return items;
	});

	public readonly userButtons = computed<readonly IButton[]>(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return [];
		}
		return [
			{
				title: 'Helpdesk Conversation',
				buttonType: 'primary',
				routerLink: this.actionRoute(
					(routes) => routes.helpdeskRoutes.conversation.children.form,
					sharedData.data.id
				).getUrl()
			}
		];
	});

	public readonly tableOverviews = computed<readonly OverviewTable[]>(() => {
		const userOverview = this.userOverview();

		if (!userOverview) {
			return [];
		}

		const tables: readonly OverviewTable[] = [
			this.getTable<UserDb.IUserItem>({
				title: 'Clients',
				itemType: 'User',
				items: userOverview.clients,
				avatar: (item) => item.avatar,
				subtitle: (item) =>
					[item.timezone, item.browser?.browserName, item.browser?.platform]
						.filter(isNotUndefined)
						.join(', '),
				fields: [
					{
						title: 'Last active',
						value: (item) => (item.lastActiveDate ? super.fromNow(item.lastActiveDate) : 'n/a'),
						format: 'relativeTime',
						showOnMobiles: true
					}
				]
			}),
			this.getTable<WorkoutDb.IWorkoutItem>({
				title: 'Workouts',
				itemType: 'Workout',
				items: userOverview.workouts,
				avatar: (item) => item.avatar?.[0]?.image,
				fields: []
			}),
			this.getTable<DietDb.IDietItem>({
				title: 'Diets',
				itemType: 'Diet',
				items: userOverview.diets,
				avatar: (item) => item.avatar?.[0]?.image,
				fields: []
			}),
			this.getTable<ExerciseDb.IExerciseItem>({
				title: 'Exercises',
				itemType: 'Exercise',
				items: userOverview.exercises,
				avatar: (item) => item.images?.[0]?.image,
				fields: []
			}),
			this.getTable<FoodDb.IFoodItem>({
				title: 'Foods',
				itemType: 'Food',
				items: userOverview.foods,
				avatar: (item) => item.images?.[0]?.image,
				fields: []
			}),
			this.getTable<TaskDb.ITaskItem>({
				title: 'Tasks',
				itemType: 'Task',
				items: userOverview.tasks,
				avatar: (item) => item.avatar?.[0]?.image,
				subtitle: (item) => item.description,
				fields: []
			}),
			this.getTable<ProgressTypeDb.IProgressTypeItem>({
				title: 'Progress types',
				itemType: 'Progress Type',
				items: userOverview.progressTypes,
				fields: []
			}),
			this.getTable<PackageDb.IPackageItem>({
				title: 'Packages',
				itemType: 'Package',
				items: userOverview.packages,
				avatar: (item) => item.avatar?.[0]?.image,
				subtitle: (item) => item.description,
				fields: []
			})
		];

		return tables;
	});

	constructor() {
		super();

		this.sharedDataInit = (data) => {
			if (data?.data) {
				this.initDataOverview(data.data.id);
			}
		};
	}

	private initDataOverview(userId: string): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.get<AdminUserDataOverview>((builder) =>
					builder.withCustomApiRoute('user', 'getAdminUserOverview').withId(userId).build()
				)
				.pipe(
					map((response) => {
						this.userOverview.set(response.data);
					})
				)
		);
	}

	private getTable<TCosmosModel extends CosmosDb.ICosmosItem>(data: {
		readonly title: string;
		readonly itemType: ItemType;
		readonly items: TCosmosModel[];
		readonly avatar?: (item: TCosmosModel) => IImage | undefined;
		readonly subtitle?: (item: TCosmosModel) => string | undefined;
		readonly fields: ITableField<TCosmosModel>[];
	}): OverviewTable {
		const table: ITable<TCosmosModel> = {
			itemType: data.itemType,
			noItemsText: {
				noItems: `There are no items`,
				noFilteredItems: `No items match your filter`
			},
			search: {
				type: 'local'
			},
			items: data.items,
			avatar: (item) => {
				return {
					name: item.system.name,
					image: data.avatar?.(item)
				};
			},
			id: (item) => item.id,
			mainItemClick: (item) => {
				this.showDetailsDialog({
					avatar: data.avatar?.(item),
					item: item,
					fields: data.fields,
					subtitle: data.subtitle?.(item)
				});
			},
			title: {
				title: 'Name',
				value: (item) => item.system.name
			},
			subtitle: data.subtitle,
			fields: [
				...data.fields,
				{
					isDefaultSort: true,
					sortBy: (m) => m.system.updated,
					format: 'relativeTime',
					title: 'Updated',
					value: (m) => super.fromNow(m.system.updated)
				}
			]
		};

		const result: OverviewTable = {
			id: data.title,
			title: data.title,
			table: table,
			suffix: data.items.length.toString()
		};

		return result;
	}

	private showDetailsDialog(data: {
		readonly item: CosmosDb.ICosmosItem;
		readonly avatar: IImage | undefined;
		readonly fields: ITableField<any>[];
		readonly subtitle: string | undefined;
	}): void {
		const fieldItems: SimpleTableItem[] = [];

		for (const field of data.fields) {
			fieldItems.push({
				title: field.title,
				value: field.value(data.item),
				valueType: 'text'
			});
		}

		this.dependencies.dialogService.openTextDialog({
			title: data.item.system.name,
			avatar: data.avatar,
			subtitle: data.subtitle,
			code: {
				code: data,
				format: 'json'
			},
			tableItems: [
				{
					iconType: 'title',
					title: 'Name',
					value: data.item.system.name,
					valueType: 'text'
				},
				{
					iconType: 'id',
					title: 'Id',
					value: data.item.id,
					valueType: 'text'
				},
				{
					iconType: 'date',
					title: 'Updated',
					value: super.fromNow(data.item.system.updated),
					valueType: 'text'
				},
				{
					iconType: 'calendar',
					title: 'Created',
					value: super.formatDateTime(data.item.system.created),
					valueType: 'text'
				},
				...fieldItems
			]
		});
	}
}
